import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import Card from "components/card/Card";
import { t } from "i18next";
import SelectNew from "components/select/SelectNew";
import DataTable from "components/dataTable/DataTable";
import { ButtonNew } from "components/buttonNew";
import { IDiscount } from "api/rpc/2024-04/facilityAdmin/product/discount";
import { GetDiscount } from "api/rpc/2024-04/facilityAdmin/discount/discount";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { StatusCode } from "api/protocols";

// SHOW LIST OF CATEGORIES, DISPLAY PRODUCTS FROM CATEGORY, SELECT PRODUCTS FOR DISCOUNT EXCEPTION
interface IDiscountProductSelectProps {
  onDiscountSelect: (discounts: IDiscount[]) => void;
  selectedDiscounts?: IDiscount[];
}
export default function AutomaticDiscountSelect(props: IDiscountProductSelectProps) {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { onDiscountSelect, selectedDiscounts } = props;

  const [discounts, setDiscounts] = useState<IDiscount[]>([]);

  useEffect(() => {
    void loadDiscounts();
  }, []);

  async function loadDiscounts() {
    const discountRes = await GetDiscount({ auto_apply: true }, false);
    if (discountRes.status !== StatusCode.OK) {
      dispatch(showError("Error loading discounts"));
      return;
    }

    setDiscounts(discountRes.data);
  }

  function handleSelectDiscounts(newSelectedDiscount: IDiscount) {
    let updated;

    if (selectedDiscounts.some(product => product.id === newSelectedDiscount.id)) {
      updated = selectedDiscounts.filter(product => product.id !== newSelectedDiscount.id);
    } else {
      updated = [...selectedDiscounts, newSelectedDiscount];
    }

    onDiscountSelect(updated);
  }

  function handleMultiValue() {
    const ids = [];
    for (let i = 0; i < selectedDiscounts.length; i++) {
      ids.push(discounts.find(discount => discount.id === selectedDiscounts[i].id)?.id);
    }

    return ids;
  }

  function getPlaceholderTitles() {
    const discountTitles = [];
    for (let i = 0; i < selectedDiscounts.length; i++) {
      discountTitles.push(discounts.find(discount => discount.id === selectedDiscounts[i].id)?.title ?? "");
    }

    return discountTitles.join(", ");
  }

  const automaticDiscountAction = {
    content: t("New Discount"),
    action: () => history.push("/admin/settings/discount/new"),
  };

  return (
    <Card
      title={t("Discounts")}
      subtitle={t("Select discounts to automatically apply to this customer type.")}
      titleActions={[automaticDiscountAction]}
    >
      <Card.Section>
        <SelectNew
          id="discounts"
          options={
            discounts?.map(discount => ({
              id: discount.id,
              label: `${discount.title}`,
              extraValue: discount,
            })) ?? []
          }
          multiValue={handleMultiValue()}
          onChange={(_, extraValue) => handleSelectDiscounts(extraValue)}
          placeholder={discounts.length > 0 ? getPlaceholderTitles() : t("No Discounts Selected")}
          helpText={t("Automatically applied when this customer type is used.")}
        />
      </Card.Section>

      {selectedDiscounts.length > 0 && (
        <div className="pr-3 pl-3 pb-3">
          <DataTable
            columns={[{ label: t("Title") }, { label: "", width: "5%" }, { label: "", width: "5%" }]}
          >
            {selectedDiscounts.map(discount => (
              <tr key={discount.id}>
                <td>{discount.title ?? discount.title}</td>
                <td>
                  <ButtonNew type="text" onClick={() => history.push(`/admin/settings/discount/${discount.id}`)}>
                    {t("Edit")}
                  </ButtonNew>
                </td>
                <td>
                  <ButtonNew type="text" onClick={() => handleSelectDiscounts(discount)}>
                    {t("Remove")}
                  </ButtonNew>
                </td>
              </tr>
            ))}
          </DataTable>
        </div>
      )}
    </Card>
  );
}
